import {useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {
    Alert,
    Backdrop,
    Box,
    Button,
    CircularProgress,
    IconButton,
    Stack,
    Typography,
    useMediaQuery,
    styled,
} from '@mui/material';
import {DateTimePicker} from '@mui/x-date-pickers';
import moment from 'moment';
import {formatFormExpirationDate} from '../../../utility/dateUtil';
import PropTypes from 'prop-types';
import FormAccessSettings from './FormAccessSettings';
import ApprovalSettings from './PrivateFormSettings/ApprovalSettings';
import AutoresponderSettings from './PrivateFormSettings/AutoresponderSettings';
import {
    getFormUrl,
    getIFrame,
    PublicEmbedSettingsSection,
    PublicLinkSettingsSection,
    shouldShowPublicSharingSettings
} from './PublicFormSettings';
import CancelIcon from '@mui/icons-material/Close';
import AdmicityDialog from '../../../shared-components/AdmicityDialog';
import {
    useGetFormQuery,
    useUpdateFormMutation,
    useUpdateFormStatusMutation,
} from '../../../api/services/formsService';
import {
    useGetExternalUserFormAssignmentsQuery,
    useGetFormEmployeeAssignmentsQuery,
    useGetFormQuestionAssignmentQuery,
    useGetFormStudentsAssignmentsQuery,
} from '../../../api/services/formAssignmentsService';
import {FORM_STATUSES} from '../../../constants/formStatuses';
import {FORM_ACCESS_LEVELS} from '../../../constants/formAccessLevels';
import FORM_AUDIENCE_TYPE from '../../../constants/FormAudienceType';
import FORM_ASSIGNMENT_TYPE from '../../../constants/FormAssignmentType';
import useNotification from '../../../utility/hooks/useNotification';

const SETTINGS_SIDE_CLEARANCE = 'clamp(160px, 16vw, 240px)';
const SETTINGS_CONTENT_MAX_WIDTH = '1800px';
const FOOTER_ACTION_BUTTON_WIDTH = 170;
const FOOTER_ACTION_BUTTON_WIDTH_RESPONSIVE = {xs: 165, lg: FOOTER_ACTION_BUTTON_WIDTH};
const FORMS_INACTIVE_TAB_INDEX = 2;

const StickyFooter = styled('div')(({theme}) => ({
    position: 'sticky',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: theme.palette.background.paper,
    borderTop: '1px solid #e0e0e0',
    padding: theme.spacing(2),
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    zIndex: 999,
}));

const FormPostCreateSetup = ({formIdOverride, onBackToBuilder}) => {
    const {id: routeId} = useParams();
    const navigate = useNavigate();
    const formId = formIdOverride != null ? Number(formIdOverride) : Number(routeId);
    const formAccessRef = useRef(null);
    const scrollContainerRef = useRef(null);
    const isSmallScreen = useMediaQuery('(max-width:1100px)');
    const {
        showErrorNotification,
        showInfoNotification,
        showSuccessNotification,
        showDetailedErrorNotification,
    } = useNotification();

    const {
        data: fetchedForm,
        isLoading: isLoadingForm,
        isError: isFormError,
    } = useGetFormQuery(formId, {skip: !formId});

    const [form, setForm] = useState(null);
    const [enableAutoresponder, setEnableAutoresponder] = useState(false);
    const [isScheduleGoLiveDialogOpen, setIsScheduleGoLiveDialogOpen] = useState(false);
    const [scheduledGoLiveDate, setScheduledGoLiveDate] = useState(null);
    const [scheduledGoLiveDateError, setScheduledGoLiveDateError] = useState('');
    const [isAtBottom, setIsAtBottom] = useState(false);

    const [updateForm, {isLoading: isSavingForm}] = useUpdateFormMutation();
    const [updateFormStatus, {isLoading: isPublishingForm}] = useUpdateFormStatusMutation();

    useEffect(() => {
        if (!fetchedForm) {
            return;
        }

        setForm(fetchedForm);
        setEnableAutoresponder(!!fetchedForm.enableAutoresponder);
        setScheduledGoLiveDate(
            fetchedForm.scheduledActivationAt
                ? moment(fetchedForm.scheduledActivationAt)
                : null
        );
        setScheduledGoLiveDateError('');
    }, [fetchedForm]);

    useEffect(() => {
        const ENTER_THRESHOLD = 220;
        const EXIT_THRESHOLD = 320;

        const getScrollDistanceFromBottom = () => {
            const container = scrollContainerRef.current;

            if (container && container.scrollHeight > container.clientHeight + 5) {
                return container.scrollHeight - (container.scrollTop + container.clientHeight);
            }

            const doc = document.documentElement;
            const scrollTop = doc.scrollTop || document.body.scrollTop;
            const scrollHeight = doc.scrollHeight || document.body.scrollHeight;
            const clientHeight = doc.clientHeight;

            return scrollHeight - (scrollTop + clientHeight);
        };

        const handleScroll = () => {
            const distanceFromBottom = getScrollDistanceFromBottom();

            setIsAtBottom(prev => {
                if (!prev && distanceFromBottom < ENTER_THRESHOLD) return true;
                if (prev && distanceFromBottom > EXIT_THRESHOLD) return false;
                return prev;
            });
        };

        const container = scrollContainerRef.current;

        window.addEventListener('scroll', handleScroll, {passive: true});
        container?.addEventListener('scroll', handleScroll, {passive: true});
        requestAnimationFrame(handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
            container?.removeEventListener('scroll', handleScroll);
        };
    }, [formId, form?.id]);

    const audience = form?.audience ?? FORM_AUDIENCE_TYPE.none;
    const assignmentType = form?.assignmentType ?? FORM_ASSIGNMENT_TYPE.students;
    const accessLevel = form?.accessLevel ?? null;
    const isPrivateForm = accessLevel === FORM_ACCESS_LEVELS.private;
    const isCommunicationForm = !!form?.isCommunication;
    const linkToStudents = !!(form?.linkToStudents ?? form?.LinkToStudents);
    const linkToEmployees = !!(form?.linkToEmployees ?? form?.LinkToEmployees);
    const templateType = form?.template?.type ?? null;
    const expirationMoment = form?.expirationDate ? moment(form.expirationDate) : null;
    const isExpirationInPast = !!expirationMoment && moment().isAfter(expirationMoment);
    const isInactive = form?.status === FORM_STATUSES.inactive;
    const isArchived = form?.status === FORM_STATUSES.archived;
    const isExpired = form?.status === FORM_STATUSES.expired;
    const isReadOnlyForm = isArchived || isExpired;

    const fetchStudentAssignments =
        formId &&
        (
            audience === FORM_AUDIENCE_TYPE.students ||
            audience === FORM_AUDIENCE_TYPE.parentsForChildCompletion ||
            audience === FORM_AUDIENCE_TYPE.enquiries ||
            audience === FORM_AUDIENCE_TYPE.previousSchool ||
            (
                audience === FORM_AUDIENCE_TYPE.externalUsers &&
                assignmentType === FORM_ASSIGNMENT_TYPE.students
            )
        );

    const fetchEmployeeAssignments =
        formId &&
        (
            audience === FORM_AUDIENCE_TYPE.employees ||
            (
                audience === FORM_AUDIENCE_TYPE.externalUsers &&
                assignmentType === FORM_ASSIGNMENT_TYPE.employees
            )
        );

    const {data: studentAssignments} = useGetFormStudentsAssignmentsQuery(
        {formId},
        {skip: !fetchStudentAssignments}
    );

    const {data: employeeAssignments} = useGetFormEmployeeAssignmentsQuery(
        {formId},
        {skip: !fetchEmployeeAssignments}
    );

    const {data: externalUserAssignments = []} = useGetExternalUserFormAssignmentsQuery(
        {formId},
        {skip: !formId || audience !== FORM_AUDIENCE_TYPE.externalUsers}
    );

    const {data: formQuestionAssignment} = useGetFormQuestionAssignmentQuery(formId, {
        skip: !formId
    });

    const recipientAssignmentData = useMemo(() => {
        if (assignmentType === FORM_ASSIGNMENT_TYPE.employees) {
            return employeeAssignments ?? {
                personAssignments: [],
                groupFilters: []
            };
        }

        return studentAssignments ?? {
            personAssignments: [],
            groupFilters: []
        };
    }, [assignmentType, employeeAssignments, studentAssignments]);

    const hasRecipients = useMemo(() => {
        const hasAssignedPeople = (recipientAssignmentData?.personAssignments?.length ?? 0) > 0;
        const hasGroupFilters = (recipientAssignmentData?.groupFilters?.length ?? 0) > 0;
        const hasExternalRecipients = (externalUserAssignments?.length ?? 0) > 0;

        return hasAssignedPeople || hasGroupFilters || hasExternalRecipients;
    }, [externalUserAssignments, recipientAssignmentData]);

    const isTriggerForm = !!formQuestionAssignment?.id;
    const isPublicTemplateForm =
        templateType === 'shareable' ||
        templateType === 'application';
    const canMakeLiveWithoutRecipients = isPublicTemplateForm || isTriggerForm;
    const showPublicSharingSettings = shouldShowPublicSharingSettings({templateType});
    const showApprovalSettings = !isCommunicationForm && !showPublicSharingSettings;
    const publicFormUrl = useMemo(() => formId ? getFormUrl(formId) : '', [formId]);
    const publicFormIframe = useMemo(() => formId ? getIFrame(formId) : '', [formId]);
    const recipientType =
        audience === FORM_AUDIENCE_TYPE.externalUsers
            ? (assignmentType === FORM_ASSIGNMENT_TYPE.employees ? 'employee' : 'student')
            : null;

    const copyToClipBoard = async (src, label) => {
        try {
            await navigator.clipboard.writeText(src);
            showSuccessNotification(`${label} copied to clipboard`);
        } catch (error) {
            if (showDetailedErrorNotification) {
                showDetailedErrorNotification(
                    'Unable to copy to clipboard',
                    error?.message || 'Please copy the value manually.'
                );
            } else {
                showErrorNotification('Unable to copy to clipboard');
            }
        }
    };

    const navigateToFormsPage = (activeTabIndex = isCommunicationForm ? 5 : 1) => {
        navigate('/forms', {state: {activeTabIndex}});
    };

    const buildUpdatePayload = (scheduledActivationAtOverride) => ({
        id: form.id,
        schema: form.schema,
        name: form.name,
        remindInterval: form.remindInterval,
        categoryIds: form.categories?.map(category => category.id) ?? [],
        strictRecipientsFiltering: !!form.strictRecipientsFiltering,
        allParentsCanSubmit: !!form.allParentsCanSubmit,
        sendToPriority1ContactsOnly: !!form.sendToPriority1ContactsOnly,
        visibleToParents: form.visibleToParents ?? true,
        shareResponseWithOtherParents: form.shareResponseWithOtherParents ?? true,
        allowMultipleSubmissions: !!form.allowMultipleSubmissions,
        enableRecipientNotifications: form.enableRecipientNotifications ?? true,
        audience: form.audience,
        parentAudienceType: form.parentAudienceType,
        accessLevel: form.accessLevel,
        expirationDate: form.expirationDate,
        notificationEmails: form.notificationEmails ?? [],
        assignmentType: form.assignmentType ?? FORM_ASSIGNMENT_TYPE.students,
        enableAutoresponder,
        scheduledActivationAt:
            scheduledActivationAtOverride !== undefined
                ? scheduledActivationAtOverride
                : form.scheduledActivationAt,
        isCommunication: !!form.isCommunication,
        profileMustBeComplete: !!form.profileMustBeComplete,
        linkToStudents: !!(form.linkToStudents ?? form.LinkToStudents),
        linkToEmployees: !!(form.linkToEmployees ?? form.LinkToEmployees),
        showResponsesInLinkedProfile:
            !!(form.showResponsesInLinkedProfile ?? form.ShowResponsesInLinkedProfile),
        showResponsesToLinkedParents:
            !!(form.showResponsesToLinkedParents ?? form.ShowResponsesToLinkedParents),
        allLinkedEmployeesCanSubmit: !!(form.allLinkedEmployeesCanSubmit ?? form.AllLinkedEmployeesCanSubmit),
        linkedStrictFiltering: !!form.linkedStrictFiltering,
    });

    const saveSetupChanges = async ({
        scheduledActivationAtOverride,
        suppressSuccessNotification = false,
        skipAccessSave = false,
    } = {}) => {
        if (!form) {
            return false;
        }

        try {
            await updateForm(buildUpdatePayload(scheduledActivationAtOverride)).unwrap();

            if (!skipAccessSave && isPrivateForm && formAccessRef.current) {
                await formAccessRef.current.saveAccessSettings({suppressSuccessNotification: true});
            }

            const nextScheduledActivationAt =
                scheduledActivationAtOverride !== undefined
                    ? scheduledActivationAtOverride
                    : form.scheduledActivationAt;

            setForm(prev => ({
                ...prev,
                enableAutoresponder,
                scheduledActivationAt: nextScheduledActivationAt,
            }));

            if (!suppressSuccessNotification) {
                showSuccessNotification('Form setup saved');
            }

            return true;
        } catch {
            showErrorNotification('Failed to save form setup');
            return false;
        }
    };

    const handleBack = async () => {
        if (onBackToBuilder) {
            onBackToBuilder();
            return;
        }

        const saved = await saveSetupChanges({suppressSuccessNotification: true});

        if (!saved) {
            return;
        }

        navigate(`/forms/edit/${formId}`, {state: {fromTabIndex: 0}});
    };

    const handleGoLiveNow = async () => {
        if (!formId) {
            return;
        }

        const saved = await saveSetupChanges({suppressSuccessNotification: true});

        if (!saved) {
            return;
        }

        try {
            await updateFormStatus({id: formId}).unwrap();
            showInfoNotification(isCommunicationForm ? 'Communication sent' : 'Form is now live');
            navigateToFormsPage();
        } catch (error) {
            showErrorNotification(error?.data ?? 'Failed to publish form');
        }
    };

    const handleMakeLiveLater = async () => {
        navigateToFormsPage(FORMS_INACTIVE_TAB_INDEX);
    };

    const handleScheduleGoLive = async (isoDate) => {
        const saved = await saveSetupChanges({
            scheduledActivationAtOverride: isoDate,
            suppressSuccessNotification: true,
        });

        if (!saved) {
            return false;
        }

        showSuccessNotification(isoDate == null ? 'Scheduled live date removed' : 'Live date scheduled');
        navigateToFormsPage();
        return true;
    };

    const handleScheduledGoLiveDateChange = (newDate) => {
        setScheduledGoLiveDate(newDate);

        if (!newDate || !expirationMoment) {
            setScheduledGoLiveDateError('');
            return;
        }

        if (newDate.isAfter(expirationMoment)) {
            setScheduledGoLiveDateError('Activation date must be on or before the expiration date.');
            return;
        }

        setScheduledGoLiveDateError('');
    };

    const handleConfirmScheduleGoLive = async () => {
        if (!scheduledGoLiveDate && !form?.scheduledActivationAt) {
            return;
        }

        const isSuccessful = await handleScheduleGoLive(
            scheduledGoLiveDate ? scheduledGoLiveDate.toDate().toISOString() : null
        );

        if (isSuccessful) {
            setIsScheduleGoLiveDialogOpen(false);
        }
    };

    const isGoLiveNowUnavailable =
        !isInactive ||
        isSavingForm ||
        isPublishingForm ||
        (!hasRecipients && !canMakeLiveWithoutRecipients) ||
        isExpirationInPast;

    const isScheduleGoLiveUnavailable =
        !isInactive ||
        isSavingForm ||
        isPublishingForm ||
        isExpirationInPast;

    const getBackdropMessage = () => {
        if (isSavingForm) return 'Saving form';
        if (isPublishingForm) return 'Publishing form';
        return '';
    };

    const renderGoLiveActionButtons = () => (
        <Stack spacing={1.25}>
            <Button
                variant="outlined"
                sx={{width: FOOTER_ACTION_BUTTON_WIDTH_RESPONSIVE, px: {xs: 0, lg: 1}}}
                disabled={isScheduleGoLiveUnavailable}
                onClick={() => setIsScheduleGoLiveDialogOpen(true)}
            >
                Schedule Go Live
            </Button>
            <Button
                variant="contained"
                sx={{
                    width: FOOTER_ACTION_BUTTON_WIDTH_RESPONSIVE,
                    px: {xs: 0, lg: 1},
                    boxShadow: 'none',
                    '&:hover': {
                        backgroundColor: '#155FA7',
                        boxShadow: 'none',
                    },
                }}
                disabled={isGoLiveNowUnavailable}
                onClick={handleGoLiveNow}
            >
                Make live now
            </Button>
        </Stack>
    );

    const renderBackActionButtons = () => (
        <Stack spacing={1.25}>
            <Button
                variant="outlined"
                sx={{width: FOOTER_ACTION_BUTTON_WIDTH_RESPONSIVE, px: {xs: 0, lg: 1}}}
                disabled={isSavingForm || isPublishingForm}
                onClick={handleMakeLiveLater}
            >
                Make live Later
            </Button>
            {renderBackToFormButton()}
        </Stack>
    );

    const renderBackToFormButton = () => (
        <Button
            variant="outlined"
            sx={{width: FOOTER_ACTION_BUTTON_WIDTH_RESPONSIVE, px: {xs: 0, lg: 1}}}
            onClick={handleBack}
            disabled={isSavingForm || isPublishingForm}
        >
            Back
        </Button>
    );

    if (isFormError) {
        return (
            <Box sx={{p: 3}}>
                <Alert severity="error">Failed to load this form.</Alert>
            </Box>
        );
    }

    if (isLoadingForm || !form) {
        return (
            <Box sx={{display: 'flex', justifyContent: 'center', py: 8}}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flexGrow: 1,
                    minHeight: 0,
                    backgroundColor: '#fff',
                }}
            >
                <Box
                    sx={{
                        p: 3,
                        borderBottom: '1px solid #e0e0e0',
                        backgroundColor: '#fff',
                    }}
                >
                    <Typography variant="h5" sx={{fontWeight: 600}}>
                        Create Form
                    </Typography>
                    {form.name && (
                        <Typography variant="body2" color="text.secondary" sx={{mt: 0.5}}>
                            {form.name}
                        </Typography>
                    )}
                </Box>

                <Box
                    ref={scrollContainerRef}
                    sx={{
                        flex: 1,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        py: 3,
                        px: {
                            xs: 3,
                            md: SETTINGS_SIDE_CLEARANCE,
                        }
                    }}
                >
                    <Stack
                        spacing={3}
                        sx={{
                            width: '100%',
                            maxWidth: SETTINGS_CONTENT_MAX_WIDTH,
                            mx: 'auto'
                        }}
                    >
                        {!hasRecipients && !canMakeLiveWithoutRecipients && (
                            <Alert severity="warning">
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        alignItems: 'center',
                                        columnGap: 0.75,
                                        rowGap: 0.5,
                                    }}
                                >
                                    <Typography component="span" variant="body2">
                                        Add at least one recipient before making this form live.
                                    </Typography>
                                    <Button
                                        color="primary"
                                        size="small"
                                        onClick={() => navigate(`/forms/edit/${formId}`)}
                                        sx={{
                                            minWidth: 'auto',
                                            p: 0,
                                            textTransform: 'none',
                                            textDecoration: 'underline',
                                            fontWeight: 400,
                                            '&:hover': {
                                                textDecoration: 'underline',
                                                backgroundColor: 'transparent',
                                            },
                                        }}
                                    >
                                        Go to form
                                    </Button>
                                </Box>
                            </Alert>
                        )}

                        <FormAccessSettings
                            embedded
                            autoSave
                            ref={formAccessRef}
                            formId={formId}
                            formName={form.name}
                        />

                        {showApprovalSettings && (
                            <ApprovalSettings
                                embedded
                                formId={formId}
                                schema={form.schema}
                                isFormLive={form.status === FORM_STATUSES.active}
                                isFormPrivate={isPrivateForm}
                                isFormArchived={isReadOnlyForm}
                            />
                        )}

                        {!isCommunicationForm && (
                            <AutoresponderSettings
                                embedded
                                formId={formId}
                                schema={form.schema}
                                enableAutoresponder={enableAutoresponder}
                                onAutoresponderToggle={setEnableAutoresponder}
                                accessLevel={accessLevel}
                                isFormArchived={isReadOnlyForm}
                                audience={audience}
                                recipientType={recipientType}
                                linkedToStudents={linkToStudents}
                                linkedToEmployees={linkToEmployees}
                            />
                        )}

                        {showPublicSharingSettings && !isCommunicationForm && (
                            <>
                                <PublicLinkSettingsSection
                                    formUrl={publicFormUrl}
                                    onCopy={copyToClipBoard}
                                />
                                <PublicEmbedSettingsSection
                                    iframe={publicFormIframe}
                                    onCopy={copyToClipBoard}
                                />
                            </>
                        )}
                    </Stack>
                </Box>

                {isAtBottom && (
                    <StickyFooter>
                        {renderBackActionButtons()}
                        <Box sx={{ml: 'auto'}}>
                            {renderGoLiveActionButtons()}
                        </Box>
                    </StickyFooter>
                )}
            </Box>

            {!isAtBottom && !isSmallScreen && (
                <Box
                    sx={(theme) => ({
                        position: 'fixed',
                        bottom: theme.spacing(2),
                        left: theme.spacing(2),
                        right: theme.spacing(2),
                        zIndex: theme.zIndex.drawer + 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-end',
                        pointerEvents: 'none',
                    })}
                >
                    <Box
                        sx={(theme) => ({
                            pointerEvents: 'auto',
                            paddingLeft: theme.spacing(2),
                        })}
                    >
                        {renderBackActionButtons()}
                    </Box>
                    <Box
                        sx={(theme) => ({
                            pointerEvents: 'auto',
                            paddingRight: theme.spacing(2),
                        })}
                    >
                        {renderGoLiveActionButtons()}
                    </Box>
                </Box>
            )}

            <AdmicityDialog
                open={isScheduleGoLiveDialogOpen}
                handleClose={() => setIsScheduleGoLiveDialogOpen(false)}
                title="Schedule Go Live"
                maxWidth="md"
                actions={[
                    {
                        label: 'Cancel',
                        onClick: () => setIsScheduleGoLiveDialogOpen(false),
                    },
                    {
                        label:
                            !scheduledGoLiveDate && form?.scheduledActivationAt
                                ? 'Remove Live Date'
                                : 'Schedule Go Live',
                        onClick: handleConfirmScheduleGoLive,
                        disabled:
                            (!scheduledGoLiveDate && !form?.scheduledActivationAt) ||
                            !!scheduledGoLiveDateError ||
                            isScheduleGoLiveUnavailable,
                    },
                ]}
            >
                <Stack spacing={2} mt={1}>
                    {expirationMoment && (
                        <Alert severity="info">
                            This form expires on {formatFormExpirationDate(form.expirationDate)}. The activation date must be on or before this.
                        </Alert>
                    )}
                    <Typography variant="body2" color="text.secondary">
                        Choose when this form should go live.
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Box sx={{flexGrow: 1}}>
                            <DateTimePicker
                                label="Activation date"
                                format="MMM D, YYYY, HH:mm"
                                value={scheduledGoLiveDate}
                                onChange={handleScheduledGoLiveDateChange}
                                minDateTime={moment()}
                                maxDateTime={expirationMoment ?? undefined}
                                ampm={false}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        helperText: scheduledGoLiveDateError
                                            ? scheduledGoLiveDateError
                                            : 'Form activates at the selected time when it becomes live.',
                                        inputProps: {readOnly: true},
                                    },
                                    field: {readOnly: true},
                                }}
                            />
                        </Box>

                        {scheduledGoLiveDate && (
                            <IconButton
                                aria-label="Clear activation date"
                                onClick={() => {
                                    setScheduledGoLiveDate(null);
                                    setScheduledGoLiveDateError('');
                                }}
                                size="small"
                                sx={{
                                    transform: 'translateY(-10px)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <CancelIcon fontSize="small" />
                            </IconButton>
                        )}
                    </Stack>
                </Stack>
            </AdmicityDialog>

            <Backdrop
                sx={{
                    color: '#fff',
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                }}
                open={isSavingForm || isPublishingForm}
            >
                <Stack flexDirection="column" alignItems="center">
                    <CircularProgress size={40} />
                    <Typography>{getBackdropMessage()}</Typography>
                </Stack>
            </Backdrop>
        </>
    );
};

FormPostCreateSetup.propTypes = {
    formIdOverride: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onBackToBuilder: PropTypes.func,
};

export default FormPostCreateSetup;
